import React, { useState, useEffect } from 'react';
import { FaTimes, FaShoppingCart, FaExclamationTriangle } from 'react-icons/fa';
import { ProfileListing } from './ProfileCard';
import { formatPrice } from '@/utils/formatPrice';
import './PurchaseModal.scss';

interface PurchaseModalProps {
  profile: ProfileListing | null;
  userBalance: number;
  onClose: () => void;
  onConfirm: (profile: ProfileListing, customPrice: number) => void;
  isProcessing?: boolean;
}

const PurchaseModal: React.FC<PurchaseModalProps> = ({
  profile,
  userBalance,
  onClose,
  onConfirm,
  isProcessing = false,
}) => {
  if (!profile) return null;

  // Buyer must pay at least 10% more than the current price
  // The amount they pay becomes the new listed price
  // Use integer math to avoid floating-point precision issues
  // Match backend calculation exactly
  const minimumPrice = Math.round(profile.price * 1.1 * 100000000) / 100000000;

  // State for custom price input
  const [customPrice, setCustomPrice] = useState<string>(minimumPrice.toFixed(8));
  const [priceError, setPriceError] = useState<string>('');
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
  const [showBreakdown, setShowBreakdown] = useState<boolean>(false);

  // Update custom price when profile changes
  useEffect(() => {
    setCustomPrice(minimumPrice.toFixed(8));
    setPriceError('');
    setShowConfirmation(false);
  }, [profile.id]);

  const formatNumber = (num: number): string => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    }
    if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    }
    return num.toLocaleString();
  };

  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCustomPrice(value);

    // Validate price
    const numValue = parseFloat(value);
    console.log(`profliePrice: ${profile.price * 1.1} numVal: ${numValue} & minPrice: ${minimumPrice} `)
    if (isNaN(numValue)) {
      setPriceError('Please enter a valid number');
    } else if (numValue < minimumPrice) {
      setPriceError(`Price must be at least ${formatPrice(minimumPrice)} (10% increase)`);
    } else {
      setPriceError('');
    }
  };

  const customPriceNum = parseFloat(customPrice) || 0;

  // Estimate network fees in KLV (kApp + bandwidth)
  // Smart contract calls typically cost ~10 KLV on Klever testnet.
  // Backend serves price values in KLV terms post-cutover, so no token
  // conversion is needed at this layer — fees and profile price are now
  // in the same denomination.
  const estimatedNetworkFeesKLV = 10;
  const totalCostKlv = customPriceNum + estimatedNetworkFeesKLV;

  const hasInsufficientFunds = userBalance < totalCostKlv;
  const isPriceValid = customPriceNum >= minimumPrice && !isNaN(customPriceNum);

  const handleConfirmClick = () => {
    if (!showConfirmation) {
      // First click - show confirmation
      setShowConfirmation(true);
    } else {
      // Second click - actually confirm purchase
      onConfirm(profile, customPriceNum);
    }
  };

  const handleCancelClick = () => {
    if (showConfirmation) {
      // If showing confirmation, just hide it
      setShowConfirmation(false);
    } else {
      // Otherwise close the modal
      onClose();
    }
  };

  return (
    <div className="purchase-modal-overlay" onClick={onClose}>
      <div className="purchase-modal" onClick={(e) => e.stopPropagation()}>
        {/* Close Button */}
        <button className="modal-close-btn" onClick={onClose} type="button">
          <FaTimes />
        </button>

        {/* Header */}
        <div className="modal-header">
          <FaShoppingCart className="modal-icon" />
          <h2>Confirm Purchase</h2>
        </div>

        {/* Profile Preview */}
        <div className="modal-profile-preview">
          <div className="preview-avatar">
            {profile.creatorAvatarUrl ? (
              <img src={profile.creatorAvatarUrl} alt={profile.creatorUsername} />
            ) : (
              <div className="avatar-placeholder">
                {profile.creatorDisplayName?.[0] || profile.creatorUsername[0]}
              </div>
            )}
          </div>
          <div className="preview-info">
            <h3>{profile.creatorDisplayName || profile.creatorUsername}</h3>
            <p className="owner-text">Owned by {profile.ownerDisplayName || profile.ownerUsername}</p>
            <div className="preview-badges">
              {profile.badges.verified && <span className="badge-verified">✓ Verified</span>}
              {profile.badges.topProfile && <span className="badge-top">⭐ Top Profile</span>}
              {profile.badges.trending && <span className="badge-trending">🔥 Trending</span>}
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="modal-stats">
          <div className="stat-row">
            <span className="stat-label">Klyx Value:</span>
            <span className="stat-value">{formatNumber(profile.klyxValue)}</span>
          </div>
          <div className="stat-row">
            <span className="stat-label">Purchase Count:</span>
            <span className="stat-value">{formatNumber(profile.purchaseCount)}</span>
          </div>
        </div>

        {/* Price and Balance */}
        <div className="modal-pricing">
          <div className="pricing-row">
            <span className="pricing-label">Current Price:</span>
            <span className="pricing-value price">{formatPrice(profile.price)}</span>
          </div>
          <div className="pricing-row">
            <span className="pricing-label">Minimum Price (10% increase):</span>
            <span className="pricing-value price">{formatPrice(minimumPrice)}</span>
          </div>

          {/* Custom Price Input */}
          <div className="price-input-container">
            <label htmlFor="customPrice" className="price-input-label">
              Your Offer:
            </label>
            <div className="price-input-wrapper">
              <input
                id="customPrice"
                type="number"
                step="0.0001"
                min={minimumPrice}
                value={customPrice}
                onChange={handlePriceChange}
                className={`price-input ${priceError ? 'error' : ''}`}
                disabled={isProcessing}
              />
              <span className="price-input-suffix">KLV</span>
            </div>
            {priceError && <span className="price-input-error">{priceError}</span>}
          </div>

          {/* Cost Breakdown */}
          {isPriceValid && (
            <div className="cost-breakdown">
              <div className="breakdown-row total-row">
                <span className="breakdown-label">Total Cost: </span>
                <span className="breakdown-value total">{formatPrice(totalCostKlv, { exact: true })}</span>
              </div>

              <button
                type="button"
                className="breakdown-toggle"
                onClick={() => setShowBreakdown((v) => !v)}
                aria-expanded={showBreakdown}
                aria-controls="cost-breakdown-details"
              >
                {showBreakdown ? '▴ Hide fee breakdown' : '▾ View fee breakdown'}
              </button>

              {showBreakdown && (
                <div id="cost-breakdown-details" className="breakdown-details">
                  <div className="breakdown-row">
                    <span className="breakdown-label">Profile Price:</span>
                    <span className="breakdown-value">{formatPrice(customPriceNum, { exact: true })}</span>
                  </div>
                  <div className="breakdown-row fee-split">
                    <span className="breakdown-label">• Seller (95%):</span>
                    <span className="breakdown-value">{formatPrice(customPriceNum * 0.95, { exact: true })}</span>
                  </div>
                  <div className="breakdown-row fee-split">
                    <span className="breakdown-label">• Creator royalty (3%):</span>
                    <span className="breakdown-value">{formatPrice(customPriceNum * 0.03, { exact: true })}</span>
                  </div>
                  <div className="breakdown-row fee-split">
                    <span className="breakdown-label">• Platform fee (2%):</span>
                    <span className="breakdown-value">{formatPrice(customPriceNum * 0.02, { exact: true })}</span>
                  </div>
                  <div className="breakdown-row gas-fee">
                    <span className="breakdown-label">Est. Network Fees:</span>
                    <span className="breakdown-value">~{formatPrice(estimatedNetworkFeesKLV, { exact: true })}</span>
                  </div>
                  <div className="breakdown-note">
                    ⓘ Total includes profile price + network fees.
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="pricing-row balance-divider">
            <span className="pricing-label">Your Balance:</span>
            <span className={`pricing-value balance ${hasInsufficientFunds ? 'insufficient' : ''}`}>
              {formatPrice(userBalance)}
            </span>
          </div>
          {!hasInsufficientFunds && isPriceValid && (
            <div className="pricing-row">
              <span className="pricing-label">Balance After:</span>
              <span className="pricing-value remaining">
                {formatPrice(userBalance - totalCostKlv)}
              </span>
            </div>
          )}
        </div>

        {/* Warning for Insufficient Funds */}
        {hasInsufficientFunds && (
          <div className="insufficient-warning">
            <FaExclamationTriangle />
            <span>Insufficient funds to complete this purchase</span>
          </div>
        )}

        {/* Confirmation Warning */}
        {showConfirmation && !isProcessing && (
          <div className="confirmation-warning">
            <FaExclamationTriangle />
            <span>Are you sure you want to purchase this profile for {formatPrice(customPriceNum)}? Click "Confirm Purchase" again to proceed.</span>
          </div>
        )}

        {/* Action Buttons */}
        <div className="modal-actions">
          <button className="cancel-btn" onClick={handleCancelClick} disabled={isProcessing} type="button">
            {showConfirmation ? 'Go Back' : 'Cancel'}
          </button>
          <button
            className={`confirm-btn ${showConfirmation ? 'confirm-active' : ''}`}
            onClick={handleConfirmClick}
            disabled={isProcessing || hasInsufficientFunds || !isPriceValid}
            type="button"
          >
            {isProcessing ? 'Processing...' : showConfirmation ? 'Confirm Purchase (Final)' : 'Confirm Purchase'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PurchaseModal;
